import React, { useMemo, useState } from 'react';
import { Form } from '../api/client';
import Sparkline from './Sparkline';
import './FormList.css';

interface FormListProps {
  forms: Form[];
  onSelectForm: (form: Form) => void;
  onDeleteForm?: (id: number) => void;
  selectedId?: number;
}

const FormList: React.FC<FormListProps> = ({
  forms,
  onSelectForm,
  onDeleteForm,
  selectedId,
}) => {
  const [sort, setSort] = useState<{ key: 'name' | 'domain' | 'total' | 'approved' | 'spam' | 'channels'; direction: 'asc' | 'desc' }>({
    key: 'name',
    direction: 'asc',
  });

  const sortedForms = useMemo(() => {
    return [...(Array.isArray(forms) ? forms : [])].sort((a, b) => {
      const aValue = getSortValue(a, sort.key);
      const bValue = getSortValue(b, sort.key);
      const result = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? result : -result;
    });
  }, [forms, sort]);

  const updateSort = (key: typeof sort.key) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'name' || key === 'domain' ? 'asc' : 'desc' });
  };

  const sortIndicator = (key: typeof sort.key) => {
    if (sort.key !== key) return '';
    return sort.direction === 'asc' ? 'up' : 'down';
  };

  return (
    <div className="form-table-container">
      <table className="form-table">
        <thead>
          <tr>
            <th>
              <button className="table-sort-button" onClick={() => updateSort('name')}>Name {sortIndicator('name')}</button>
            </th>
            <th>
              <button className="table-sort-button" onClick={() => updateSort('domain')}>Domain {sortIndicator('domain')}</button>
            </th>
            <th>Trend</th>
            <th>
              <button className="table-sort-button" onClick={() => updateSort('total')}>Status {sortIndicator('total')}</button>
            </th>
            <th>
              <button className="table-sort-button" onClick={() => updateSort('channels')}>Channels {sortIndicator('channels')}</button>
            </th>
            <th>Hash</th>
            {onDeleteForm && <th className="actions-cell" style={{ width: '80px' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sortedForms.map((form) => (
            <tr
              key={form.id}
              className={selectedId === form.id ? 'selected' : ''}
              onClick={() => onSelectForm(form)}
            >
              <td className="form-name-cell">
                <div className="form-name-content">
                  <span className="form-name">{form.name}</span>
                  {form.testMode && (
                    <span className="badge badge-test">TEST</span>
                  )}
                </div>
                {form.description && (
                  <span className="form-description">{form.description}</span>
                )}
              </td>
              <td className="form-domain">{form.domain}</td>
              <td className="form-trend-cell">
                {form.stats ? (
                  <Sparkline data={form.stats.trend} width={70} height={18} />
                ) : (
                  <span className="no-data">—</span>
                )}
              </td>
              <td className="form-status-cell">
                {form.stats ? (
                  <div className="status-badges">
                    <span className="status-badge status-approved" title="Approved">
                      {form.stats.approved}
                    </span>
                    <span className="status-badge status-flagged" title="Flagged">
                      {form.stats.flagged}
                    </span>
                    <span className="status-badge status-spam" title="Spam">
                      {form.stats.spam}
                    </span>
                  </div>
                ) : (
                  <span className="no-data">—</span>
                )}
              </td>
              <td className="form-channels">
                <div className="channel-counts">
                  <span className="channel-count" title="Notification channels">{form.channelCount}</span>
                  {form.flaggedChannelCount !== undefined && form.flaggedChannelCount > 0 && (
                    <span className="channel-count flagged-channel-count" title="Flagged notification channels">
                      {form.flaggedChannelCount}⚑
                    </span>
                  )}
                  {form.spamChannelCount !== undefined && form.spamChannelCount > 0 && (
                    <span className="channel-count spam-channel-count" title="Spam notification channels">
                      {form.spamChannelCount}🚫
                    </span>
                  )}
                </div>
              </td>
              <td className="form-hash-cell">
                <code className="form-hash">{form.hash}</code>
              </td>
              {onDeleteForm && (
                <td className="form-actions actions-cell">
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteForm(form.id);
                    }}
                    title="Delete form"
                  >
                    Delete
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function getSortValue(form: Form, key: 'name' | 'domain' | 'total' | 'approved' | 'spam' | 'channels') {
  if (key === 'name') return form.name;
  if (key === 'domain') return form.domain;
  if (key === 'channels') return form.channelCount;
  if (key === 'approved') return form.stats?.approved ?? 0;
  if (key === 'spam') return form.stats?.spam ?? 0;
  return form.stats?.total ?? 0;
}

export default FormList;
